// path: dashboard/src/components/financeOfficer/FinanceStatsCards.jsx

import React, { useMemo } from "react";
import { FiTrendingUp, FiTrendingDown, FiDollarSign, FiActivity } from "react-icons/fi";
import PremiumStatCard from "../common/PremiumStatCard";
import { TrendingUp, TrendingDown, Wallet, Activity } from "lucide-react";

export default function FinanceStatsCards({ transactions, filterType }) {
  
  const stats = useMemo(() => {
    // If filtering by a specific type, use that. Otherwise, use the full list passed in.
    const data = transactions || [];
    
    const totalIncome = data
      .filter(t => t.type === 'INCOME')
      .reduce((sum, t) => sum + (Number(t.amount) || 0), 0);

    const totalExpense = data
      .filter(t => t.type === 'EXPENSE')
      .reduce((sum, t) => sum + (Number(t.amount) || 0), 0);

    const netBalance = totalIncome - totalExpense;

    // Counters for the specific type if selected
    const pendingItems = data.filter(t => 
      t.status === 'Pending' || t.status === 'unpaid' || t.status === 'draft'
    ).length;

    return {
      totalIncome,
      totalExpense,
      netBalance,
      pendingItems
    };
  }, [transactions]);

  const formatCurrency = (amount) =>
    `$${Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  // Determine if we are showing "Global" stats or "Filtered" stats in the label
  const getLabel = (defaultLabel) => {
    if (filterType && filterType !== "") return `${defaultLabel} (${filterType})`;
    if (stats.totalIncome === 0 && stats.totalExpense === 0) return `${defaultLabel} (No Data)`;
    return defaultLabel;
  };

  const cards = [
    {
      title: getLabel("Total Income"),
      value: stats.totalIncome,
      icon: <FiTrendingUp className="text-green-600" />,
      color: "bg-green-50",
      valueColor: "text-green-700",
      isCurrency: true,
    },
    {
      title: getLabel("Total Expenses"),
      value: stats.totalExpense,
      icon: <FiTrendingDown className="text-red-600" />,
      color: "bg-red-50",
      valueColor: "text-red-700",
      isCurrency: true,
    },
    {
      title: "Net Balance",
      value: stats.netBalance,
      icon: <FiDollarSign className="text-blue-600" />,
      color: "bg-blue-50",
      valueColor: "text-blue-700",
      isCurrency: true,
    },
    {
      title: "Pending Actions",
      value: `${stats.pendingItems} Items`,
      icon: <FiActivity className="text-orange-600" />,
      color: "bg-orange-50",
      valueColor: "text-orange-700",
      isCurrency: false,
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
      <PremiumStatCard 
        title="Total Income" 
        value={formatCurrency(stats.totalIncome)} 
        icon={<TrendingUp className="text-emerald-600" />} 
        iconBg="bg-emerald-50"
        description="Donation highlights"
      />
      <PremiumStatCard 
        title="Total Expenses" 
        value={formatCurrency(stats.totalExpense)} 
        icon={<TrendingDown className="text-red-600" />} 
        iconBg="bg-red-50"
        description="Accumulated costs"
      />
      <PremiumStatCard 
        title="Net Balance" 
        value={formatCurrency(stats.netBalance)} 
        icon={<Wallet className="text-blue-600" />} 
        iconBg="bg-blue-50"
        description="Current liquidity"
      />
      <PremiumStatCard 
        title="Pending Items" 
        value={`${stats.pendingItems}`} 
        icon={<Activity className="text-orange-600" />} 
        iconBg="bg-orange-50"
        description="Awaiting settlement"
      />
    </div>
  );
}
